// Small, dependency-free colour maths used by the brand-theme layer.
//
// Everything here is pure: no DOM, no store, no side effects. The only
// consumer that needs the DOM is `brand-theme.js`, which turns these
// results into CSS custom properties.
//
// Contrast maths follow WCAG 2.1 relative luminance (the sRGB
// linearisation formula), not the cheap 0.299/0.587/0.114 approximation
// — the platform is being taken through a WCAG pass and the two
// disagree materially on saturated cyans/teals, which is exactly the
// hue family the default brand colours live in.

const HEX_SHORT = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_LONG = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

// Parses `#abc`, `#aabbcc`, `abc`, `aabbcc` (any case) into
// `{ r, g, b }` with 0-255 channels. Returns null for anything else —
// including the literal strings 'null' / 'undefined', which the event
// payload has been known to carry when the admin never set a colour.
export function parseHex(value) {
  if (typeof value !== 'string') return null;

  const raw = value.trim();
  if (!raw) return null;

  const short = raw.match(HEX_SHORT);
  if (short) {
    return {
      r: parseInt(short[1] + short[1], 16),
      g: parseInt(short[2] + short[2], 16),
      b: parseInt(short[3] + short[3], 16)
    };
  }

  const long = raw.match(HEX_LONG);
  if (long) {
    return {
      r: parseInt(long[1], 16),
      g: parseInt(long[2], 16),
      b: parseInt(long[3], 16)
    };
  }

  return null;
}

// Normalises any accepted input to lowercase `#rrggbb`, or null.
export function normalizeHex(value) {
  const rgb = parseHex(value);
  return rgb ? toHex(rgb) : null;
}

export function toHex({ r, g, b }) {
  const channel = (c) => clampChannel(c).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

// Space-separated sRGB channels (`"4 120 115"`) — the form Tailwind's
// `rgb(var(--x) / <alpha-value>)` colour definitions expect, so a token
// published this way still supports the `/50` opacity modifiers.
export function toRgbChannels({ r, g, b }) {
  return `${clampChannel(r)} ${clampChannel(g)} ${clampChannel(b)}`;
}

export function toRgba({ r, g, b }, alpha) {
  return `rgba(${clampChannel(r)}, ${clampChannel(g)}, ${clampChannel(
    b
  )}, ${alpha})`;
}

function clampChannel(c) {
  return Math.max(0, Math.min(255, Math.round(c)));
}

// WCAG 2.1 relative luminance.
export function relativeLuminance({ r, g, b }) {
  const linear = (c) => {
    const s = clampChannel(c) / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

// WCAG 2.1 contrast ratio, 1..21. Accepts hex strings or rgb objects.
export function contrastRatio(a, b) {
  const rgbA = typeof a === 'string' ? parseHex(a) : a;
  const rgbB = typeof b === 'string' ? parseHex(b) : b;
  if (!rgbA || !rgbB) return 1;

  const lumA = relativeLuminance(rgbA);
  const lumB = relativeLuminance(rgbB);
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);

  return (lighter + 0.05) / (darker + 0.05);
}

// Linear blend in sRGB space. `amount` 0 → `from`, 1 → `to`.
export function mix(from, to, amount) {
  const t = Math.max(0, Math.min(1, amount));
  return {
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t
  };
}

// ── HSL ─────────────────────────────────────────────────────────────
// Lightness is the axis the contrast fixer walks: blending a saturated
// teal towards white desaturates it into grey (#047873 → #62aba8),
// whereas raising HSL lightness keeps it recognisably the brand colour
// (#047873 → a vivid teal in the same family as the approved #2dd4bf).

export function rgbToHsl({ r, g, b }) {
  const rn = clampChannel(r) / 255;
  const gn = clampChannel(g) / 255;
  const bn = clampChannel(b) / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;

  return { h, s, l };
}

export function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }

  const hue = (t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p2 + (q - p2) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p2 + (q - p2) * (2 / 3 - x) * 6;
    return p2;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p2 = 2 * l - q;

  return {
    r: hue(h + 1 / 3) * 255,
    g: hue(h) * 255,
    b: hue(h - 1 / 3) * 255
  };
}

const BLACK = { r: 0, g: 0, b: 0 };
const WHITE = { r: 255, g: 255, b: 255 };

export function darken(rgb, amount) {
  return mix(rgb, BLACK, amount);
}

export function lighten(rgb, amount) {
  return mix(rgb, WHITE, amount);
}

// Picks whichever of `light` / `dark` reads better on `background`.
// Used only as a *fallback* — when the admin has explicitly chosen a
// button text colour we honour it rather than second-guessing them.
export function readableTextOn(
  background,
  dark = '#111827',
  light = '#ffffff'
) {
  const bg = typeof background === 'string' ? parseHex(background) : background;
  if (!bg) return light;
  return contrastRatio(bg, dark) >= contrastRatio(bg, light) ? dark : light;
}

// Nudges `rgb` towards white or black — whichever direction moves *away*
// from `background` — until it clears `minRatio` against that
// background, preferring to stop as soon as it also clears
// `preferredRatio`.
//
// Hue and (broadly) saturation survive because the blend is towards a
// neutral, so the result still reads as the event's brand colour; it
// just lands at a lightness that is actually legible. A colour that
// already passes is returned untouched, which is why the shipped
// defaults (#047873 on white) render exactly as they do today.
export function ensureContrast(
  color,
  background,
  { minRatio = 4.5, preferredRatio = minRatio, steps = 40 } = {}
) {
  const rgb = typeof color === 'string' ? parseHex(color) : color;
  const bg = typeof background === 'string' ? parseHex(background) : background;
  if (!rgb) return null;
  if (!bg) return { ...rgb };

  if (contrastRatio(rgb, bg) >= preferredRatio) return { ...rgb };

  // Walk lightness away from the background: up on dark grounds, down
  // on light ones. Hue and saturation are held, so the result stays
  // recognisably the event's colour.
  const hsl = rgbToHsl(rgb);
  const towardsLight = relativeLuminance(bg) < 0.5;
  const span = towardsLight ? 1 - hsl.l : hsl.l;

  let best = { ...rgb };
  let bestRatio = contrastRatio(rgb, bg);

  for (let i = 1; i <= steps; i += 1) {
    const delta = (span * i) / steps;
    const candidate = hslToRgb({
      h: hsl.h,
      s: hsl.s,
      l: towardsLight ? hsl.l + delta : hsl.l - delta
    });
    const ratio = contrastRatio(candidate, bg);

    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
    if (ratio >= preferredRatio) return candidate;
  }

  // Hue exhausted before reaching the preferred ratio (a pure yellow on
  // white, say) — hand back the most legible point found. When even
  // that misses `minRatio`, fall back to the neutral endpoint, which
  // always passes.
  if (bestRatio >= minRatio) return best;
  return towardsLight ? { ...WHITE } : { ...BLACK };
}
